import { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  Modal,
  Form,
  Divider,
  Drawer,
  DatePicker
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { cogsService } from '../services/cogsService';
import { dimensionsService } from '../services/dimensionsService';
import CostingRuleDetails from '../components/CostingRuleDetails';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

const PendingApprovalsPage = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({ status: 'PENDING' });
  const [dimensions, setDimensions] = useState({});
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  
  const { user } = useAuth();
  const userData = user?.data;

  // Load dimensions for filters
  const loadDimensions = useCallback(async () => {
    try {
      const dimensionData = await dimensionsService.getAllDimensions();
      setDimensions(dimensionData);
    } catch (error) {
      // Silently handle dimension loading error
    }
  }, []);

  // Load pending approval rules
  const loadRules = useCallback(async (page = 1, pageSize = 20, filterParams = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        status: 'PENDING',
        ...filterParams
      };

      const response = await cogsService.getRules(params);
      setRules(response.rules || []);
      setPagination({
        current: page,
        pageSize,
        total: response.pagination?.total || 0
      });
    } catch (error) {
      message.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDimensions();
    loadRules();
  }, [loadDimensions, loadRules]);

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    const { current, pageSize } = paginationInfo;
    loadRules(current, pageSize, filters);
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  // Format date with timezone
  const formatDateWithTimezone = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Render fee structure
  const renderFeeStructure = (record) => {
    const fees = [];
    
    if (record.flatFee) {
      fees.push(`${formatCurrency(record.flatFee, record.feeCurrencyCode)}`);
    }
    
    if (record.percentageRate) {
      fees.push(`${record.percentageRate}%`);
    }
    
    if (record.minimumFee) {
      fees.push(`Min: ${formatCurrency(record.minimumFee, record.feeCurrencyCode)}`);
    }
    
    if (record.maximumFee) {
      fees.push(`Max: ${formatCurrency(record.maximumFee, record.feeCurrencyCode)}`);
    }
    
    return fees.length > 0 ? fees.join(' | ') : 'No fee structure';
  };

  // Handle rule details view
  const handleViewDetails = (ruleId) => {
    setSelectedRuleId(ruleId);
    setDetailsVisible(true);
  };

  // Handle approve rule
  const handleApproveRule = (rule) => {
    confirm({
      title: 'Approve COGS Rule',
      icon: <CheckCircleOutlined />,
      content: `Are you sure you want to approve this COGS rule for ${rule.entity?.name}?`,
      okText: 'Approve',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(true);
        try {
          await cogsService.approveRule(rule.id, 'Approved via pending approvals page');
          message.success('COGS rule approved successfully');
          loadRules(pagination.current, pagination.pageSize, filters);
        } catch (error) {
          message.error('Failed to approve COGS rule');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Handle reject rule
  const handleRejectRule = (rule) => {
    setSelectedRule(rule);
    setRejectModalVisible(true);
    form.resetFields();
  };

  // Submit rejection
  const handleRejectSubmit = async () => {
    try {
      const values = await form.validateFields();
      setActionLoading(true);
      
      await cogsService.rejectRule(selectedRule.id, values.reason);
      message.success('COGS rule rejected successfully');
      
      setRejectModalVisible(false);
      setSelectedRule(null);
      loadRules(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error('Failed to reject COGS rule');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle filter application
  const handleFilterApply = () => {
    const filterValues = filterForm.getFieldsValue();
    const processedFilters = { status: 'PENDING' }; // Always filter for pending status

    // Process date ranges
    if (filterValues.dateRange) {
      processedFilters.startDate = filterValues.dateRange[0]?.toISOString();
      processedFilters.endDate = filterValues.dateRange[1]?.toISOString();
    }

    // Process other filters
    Object.keys(filterValues).forEach(key => {
      if (key !== 'dateRange' && filterValues[key] !== undefined && filterValues[key] !== '') {
        processedFilters[key] = filterValues[key];
      }
    });

    setFilters(processedFilters);
    loadRules(1, pagination.pageSize, processedFilters);
    setFilterDrawerVisible(false);
  };

  // Clear all filters
  const handleFilterClear = () => {
    filterForm.resetFields();
    const clearedFilters = { status: 'PENDING' }; // Keep pending status filter
    setFilters(clearedFilters);
    loadRules(1, pagination.pageSize, clearedFilters);
    setFilterDrawerVisible(false);
  };

  // Check if user can approve/reject
  const canApproveReject = (rule) => {
    if (!userData) return false;
    if (!['Admin', 'Manager'].includes(userData.role)) return false;

    // Admins can approve/reject any rule, including their own (maker-approver role)
    if (userData.role === 'Admin') return true;

    // Managers can't approve/reject their own rules
    if (rule.proposerId === userData.id) return false;

    // Check entity access for managers
    return userData.accessibleEntities?.some(e => e.id === rule.entityId);
  };

  // Render costing details based on available data
  const renderCostingDetails = (record) => {
    const details = [];

    // Payout-related details
    if (record.payoutRailName) {
      details.push(`Rail: ${record.payoutRailName}`);
    }
    if (record.payoutConnectorName) {
      details.push(`Connector: ${record.payoutConnectorName}`);
    }

    // Card-related details
    if (record.acquirerName) {
      details.push(`Acquirer: ${record.acquirerName}`);
    }
    if (record.cardBrandName) {
      details.push(`Brand: ${record.cardBrandName}`);
    }
    if (record.cardType && record.cardType !== 'N/A') {
      details.push(`Type: ${record.cardType}`);
    }
    if (record.cardTier) {
      details.push(`Tier: ${record.cardTier}`);
    }
    if (record.mccs && record.mccs.length > 0) {
      details.push(`MCCs: ${record.mccs.map(m => m.mcc).join(', ')}`);
    } else if (record.mcc) {
      details.push(`MCCs: ${record.mcc}`);
    }

    // Other details
    if (record.industryName) {
      details.push(`Industry: ${record.industryName}`);
    }
    if (record.flow) {
      details.push(`Flow: ${record.flow}`);
    }
    if (record.processor) {
      details.push(`Processor: ${record.processor}`);
    }
    if (record.businessId) {
      details.push(`Business: ${record.businessId}`);
    }

    return details.length > 0 ? (
      <div>
        {details.map((detail, index) => (
          <div key={index} style={{ fontSize: '11px' }}>
            <Text type="secondary">{detail}</Text>
          </div>
        ))}
      </div>
    ) : 'N/A';
  };

  // Table columns configuration
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    {
      title: 'Entity',
      dataIndex: ['entity', 'name'],
      key: 'entity',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Text strong>{text || 'N/A'}</Text>
          {record.entity?.country && (
            <div><Tag size="small">{record.entity.country}</Tag></div>
          )}
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: ['product', 'name'],
      key: 'product',
      ellipsis: true,
    },
    {
      title: 'Pricing Scheme',
      dataIndex: ['scheme'],
      key: 'scheme',
      ellipsis: true,
      render: (scheme) => {
        if (!scheme) return 'N/A';
        if (scheme.code && scheme.description) {
          return `${scheme.code} - ${scheme.description}`;
        }
        return scheme.code || scheme.description || 'N/A';
      },
    },
    {
      title: 'Fee Structure',
      key: 'feeStructure',
      ellipsis: true,
      render: (_, record) => (
        <Text code style={{ fontSize: '12px' }}>
          {renderFeeStructure(record)}
        </Text>
      ),
    },
    {
      title: 'Costing Details',
      key: 'costingDetails',
      width: 200,
      ellipsis: true,
      render: (_, record) => renderCostingDetails(record),
    },
    {
      title: 'Currency',
      dataIndex: 'feeCurrencyCode',
      key: 'currency',
      width: 80,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search currency"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.feeCurrencyCode
          ? record.feeCurrencyCode.toString().toLowerCase().includes(value.toLowerCase())
          : false,
    },
    {
      title: 'MIDs',
      key: 'mids',
      width: 150,
      ellipsis: true,
      render: (_, record) => {
        if (!record.mids || record.mids.length === 0) {
          // Fallback to legacy mid field if no mids array
          return record.mid ? (
            <Text code style={{ fontSize: '11px' }}>
              {record.mid}{record.subMid ? ` (${record.subMid})` : ''}
            </Text>
          ) : 'N/A';
        }

        const midsText = record.mids.map(midObj =>
          midObj.subMid ? `${midObj.mid} (${midObj.subMid})` : midObj.mid
        ).join(', ');

        return (
          <Text code style={{ fontSize: '11px' }} title={midsText}>
            {midsText}
          </Text>
        );
      },
    },
    {
      title: 'Proposer',
      dataIndex: ['proposer', 'email'],
      key: 'proposer',
      ellipsis: true,
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 150,
      render: (date) => formatDateWithTimezone(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record.id)}
            />
          </Tooltip>
          {canApproveReject(record) && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="text"
                  icon={<CheckCircleOutlined />}
                  style={{ color: '#52c41a' }}
                  loading={actionLoading}
                  onClick={() => handleApproveRule(record)}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  icon={<CloseCircleOutlined />}
                  danger
                  loading={actionLoading}
                  onClick={() => handleRejectRule(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>Pending Approvals</Title>
          <Text type="secondary">
            Review and approve or reject COGS rules awaiting approval
          </Text>
        </div>

        {/* Action Bar */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col flex="auto">
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterDrawerVisible(true)}
              >
                Advanced Filters
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadRules(pagination.current, pagination.pageSize, filters)}
              >
                Refresh
              </Button>
            </Space>
          </Col>
          <Col>
            <Text type="secondary">
              {pagination.total} pending approval{pagination.total !== 1 ? 's' : ''}
            </Text>
          </Col>
        </Row>

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} pending approvals`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Rule Details Modal */}
      <CostingRuleDetails
        ruleId={selectedRuleId}
        visible={detailsVisible}
        onClose={() => {
          setDetailsVisible(false);
          setSelectedRuleId(null);
        }}
        refreshData={() => loadRules(pagination.current, pagination.pageSize, filters)}
      />

      {/* Reject Rule Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            Reject COGS Rule
          </Space>
        }
        open={rejectModalVisible}
        onOk={handleRejectSubmit}
        onCancel={() => {
          setRejectModalVisible(false);
          setSelectedRule(null);
          form.resetFields();
        }}
        confirmLoading={actionLoading}
        okText="Reject Rule"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        width={600}
      >
        {selectedRule && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Rule Details:</Text>
              <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                <div><Text strong>Entity:</Text> {selectedRule.entity?.name}</div>
                <div><Text strong>Product:</Text> {selectedRule.product?.name}</div>
                <div><Text strong>Proposer:</Text> {selectedRule.proposer?.email}</div>
                <div><Text strong>Fee Structure:</Text> {renderFeeStructure(selectedRule)}</div>
              </div>
            </div>

            <Divider />

            <Form form={form} layout="vertical">
              <Form.Item
                name="reason"
                label="Rejection Reason"
                rules={[
                  { required: true, message: 'Please provide a reason for rejection' },
                  { min: 10, message: 'Reason must be at least 10 characters long' }
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Please provide a detailed reason for rejecting this COGS rule..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        placement="right"
        width={400}
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        extra={
          <Space>
            <Button onClick={handleFilterClear}>Clear</Button>
            <Button type="primary" onClick={handleFilterApply}>
              Apply
            </Button>
          </Space>
        }
      >
        <Form form={filterForm} layout="vertical">
          <Form.Item name="search" label="Search">
            <Input
              placeholder="Search by description, ID, etc."
              prefix={<SearchOutlined />}
            />
          </Form.Item>

          <Form.Item name="entityId" label="Entity">
            <Select
              mode="multiple"
              placeholder="Select entities"
              allowClear
            >
              {dimensions.entities?.map(entity => (
                <Option key={entity.id} value={entity.id}>
                  {entity.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="productId" label="Product">
            <Select
              mode="multiple"
              placeholder="Select products"
              allowClear
            >
              {dimensions.products?.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="schemeId" label="Pricing Scheme">
            <Select
              mode="multiple"
              placeholder="Select schemes"
              allowClear
            >
              {dimensions.schemes?.map(scheme => (
                <Option key={scheme.id} value={scheme.id}>
                  {scheme.code && scheme.description
                    ? `${scheme.code} - ${scheme.description}`
                    : scheme.code || scheme.description || scheme.scheme || 'N/A'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="feeCurrencyCode" label="Currency">
            <Select
              mode="multiple"
              placeholder="Select currencies"
              allowClear
            >
              {dimensions.currencies?.map(currency => (
                <Option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="proposerId" label="Proposer">
            <Select
              placeholder="Select proposer"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {dimensions.users?.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.email}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item name="dateRange" label="Date Range">
            <RangePicker
              style={{ width: '100%' }}
              showTime
            />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default PendingApprovalsPage;
